<script>
export default {
    page() {
        const title = 'Radio'
        const description = `Underground electronic radio channels — house, techno, progressive, deep house`
        const image = `${import.meta.env.VITE_CLIENT_URL}/frisky-social-share-image.png`
        const canonicalUrl = `https://frisky.fm${this.$route.path}`
        return {
            title,
            link: [{ rel: 'canonical', vmid: 'canonical', href: canonicalUrl }],
            meta: [
                { name: 'title', vmid: 'title', content: title },
                { name: 'description', vmid: 'description', content: description },

                { property: 'og:title', vmid: 'og:title', content: title },
                { property: 'og:description', vmid: 'og:description', content: description },
                { property: 'og:image', vmid: 'og:image', content: image },
                { property: 'og:type', vmid: 'og:type', content: 'website' },
                { property: 'og:url', vmid: 'og:url', content: canonicalUrl },
                { property: 'og:site_name', vmid: 'og:site_name', content: 'FRISKY' },

                { name: 'twitter:card', vmid: 'twitter:card', content: 'summary_large_image' },
                { name: 'twitter:title', vmid: 'twitter:title', content: title },
                { name: 'twitter:description', vmid: 'twitter:description', content: description },
                { name: 'twitter:image', vmid: 'twitter:image', content: image },
                { name: 'twitter:url', vmid: 'twitter:url', content: canonicalUrl },
            ],
        }
    },
}
</script>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from '@composables/use-store'
import DataLayer from '@services/data-layer/index'
import Layout from '@layouts/main-layout'
import ChannelDetailBlock from '@components/channel/channel-detail-block'
import PremiumBanner from '@components/premium-banner'

const CHANNELS_ORDER = ['chill', 'classics', 'deep', 'frisky']
const PLACEHOLDER_CHANNELS = CHANNELS_ORDER.length

defineProps({
    isFavorites: {
        type: Boolean,
        default: false,
    },
})

const store = useStore()

const channels = ref(Array.from(Array(PLACEHOLDER_CHANNELS), (_channel, id) => ({ id })))

const playlists = computed(() => store.state.nowPlaying.byStation)

onMounted(() => {
    DataLayer.channels.get().then(response => {
        channels.value = Object.values(response.body).sort(
            (a, b) => CHANNELS_ORDER.indexOf(a.id) - CHANNELS_ORDER.indexOf(b.id)
        )
    })
})
</script>

<template>
    <Layout
        v-if="channels && channels.length"
        hidden-overflow
    >
        <BaseContent title="Radio">
            <div class="premium-banner-inset">
                <PremiumBanner source="radio-banner" />
            </div>
            <ChannelDetailBlock
                :channels="channels"
                :playlists="playlists"
                :is-favorites="isFavorites"
            />
        </BaseContent>
    </Layout>
</template>

<style lang="scss" scoped>
@import '@design';

// On the radio page the channel carousel breaks out to the full content width,
// so the banner would otherwise stretch full-bleed. The Figma (816:406) insets
// it: a fixed 1005px creative centered in the content (matches the banner's
// fixed-size art). Below 1005px of content (mobile/tablet) it stays full-width.
// Named for the wrapper rather than the component: a scoped `.premium-banner`
// would also match PremiumBanner's own root, which carries this scope id too.
.premium-banner-inset {
    max-width: 1005px;
    margin-right: auto;
    margin-left: auto;
}
</style>
